import "@/App.css";
import { useState, useEffect, useCallback, Suspense } from "react";
import { toast } from "sonner";
import { useAutoLogout, staleSessionReason, clearSessionMarkers, logoutMessage } from "@/hooks/useAutoLogout";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import PalletPublicView from "@/pages/PalletPublicView";
import PortalApp from "@/pages/Portal/PortalApp";
import { routeConfig } from "@/routes/routeConfig";

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900"></div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stale = staleSessionReason();
    if (stale) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearSessionMarkers();
      setTimeout(() => toast.warning(logoutMessage(stale), { duration: 8000 }), 300);
    }
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    const pageHeader = axios.interceptors.request.use((cfg) => {
      cfg.headers["X-Page"] = window.location.pathname;
      return cfg;
    });
    
    if (token && savedUser) {
      try {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
    return () => axios.interceptors.request.eject(pageHeader);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    if (window.location.pathname === '/login' || window.location.pathname !== '/') {
      window.location.href = '/';
    }
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    clearSessionMarkers();
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  }, []);

  useAutoLogout(user, handleLogout);

  // Permission check function
  const hasPermission = (moduleOrPermission, action = null) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    
    if (action) {
      const modulePerms = user.module_permissions;
      if (Array.isArray(modulePerms)) {
        const modulePerm = modulePerms.find(p => p.module === moduleOrPermission);
        if (modulePerm) return modulePerm[action] === true;
      }
      const permissions = user.permissions;
      if (permissions && permissions[moduleOrPermission]) {
        return permissions[moduleOrPermission][action] === true;
      }
      return false;
    }
    
    const rolePermissions = {
      admin: ["view", "entry", "manage_users", "settings"],
      entry: ["view", "entry"],
      viewer: ["view"]
    };
    
    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes(moduleOrPermission);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zinc-900"></div>
      </div>
    );
  }

  // Render a route element based on config
  const renderRouteElement = (config) => {
    const { component: Component, permission, permissionModule, passUser, passHasPermission, adminOnly } = config;
    
    // Build props
    const props = {};
    if (passUser) props.user = user;
    if (passHasPermission) props.hasPermission = hasPermission;
    
    // Check permission
    if (adminOnly && user?.role !== "admin") {
      return <Navigate to="/" replace />;
    }
    
    if (permission && !hasPermission(permission)) {
      return <Navigate to="/" replace />;
    }

    // Module-level granular permission check (admin always passes)
    if (permissionModule && user?.role !== "admin") {
      if (!hasPermission(permissionModule, "view")) {
        return <Navigate to="/" replace />;
      }
    }
    
    return (
      <Suspense fallback={<PageLoader />}>
        <Component {...props} />
      </Suspense>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
            }
          />
          <Route path="/p/:palletNo" element={<PalletPublicView />} />
          <Route path="/portal/*" element={<PortalApp />} />
          <Route
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} hasPermission={hasPermission} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            {routeConfig.map((config) => (
              <Route
                key={config.path}
                path={config.path}
                element={renderRouteElement(config)}
              />
            ))}
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
